import { createAsyncThunk, createSlice } from "@reduxjs/toolkit"
import { GetAllNotificationStatusDTO, GetAllNotificationStatusFiltersDTO, NotificationStatusDTO, NotificationStatusUpdateDTO } from "./dataservice/dto"
import { PromiseStatuses } from "../../../../utils"
import { NewNotificationStatusService } from "./dataservice/service"

interface NotificationStatusState {
    onEdit: boolean

    // REQUEST
    notificationStatusUpdateRequest: NotificationStatusUpdateDTO
    notificationStatusIdToUpdate: string

    // FILTERS
    getAllNotificationStatusFilters: GetAllNotificationStatusFiltersDTO

    // STATUS
    notificationStatusUpdateStatus: PromiseStatuses
    getAllNotificationStatusStatus: PromiseStatuses
    getNotificationStatusByIdStatus: PromiseStatuses

    // RESPONSE
    getAllNotificationStatusResponse?: GetAllNotificationStatusDTO
    getNotificationStatusByIdResponse?: NotificationStatusDTO
}

const initialState: NotificationStatusState = {
    onEdit: false,

    // REQUEST
    notificationStatusUpdateRequest: {},
    notificationStatusIdToUpdate: '',

    // FILTERS
    getAllNotificationStatusFilters: {
        itemsPerPage: 0,
        page: 0
    },

    // STATUS
    notificationStatusUpdateStatus: 'idle',
    getAllNotificationStatusStatus: 'idle',
    getNotificationStatusByIdStatus: 'idle'
}

export const NotificationStatusUpdate = createAsyncThunk(
    'notificationStatus/Update',
    async (request: {body: NotificationStatusUpdateDTO, id: string}, thunkApi): Promise<void> => {
        const notificationStatusService = NewNotificationStatusService()

        return notificationStatusService.UpdateNotificationStatus(request.body, request.id).catch(error => {
            throw (thunkApi.rejectWithValue(error))
        })
    },
)

export const GetAllNotificationStatus = createAsyncThunk(
    'notificationStatus/GetAll',
    async (filters: GetAllNotificationStatusFiltersDTO, thunkApi): Promise<GetAllNotificationStatusDTO> => {
        const notificationStatusService = NewNotificationStatusService()

        return notificationStatusService.GetAllNotificationStatus(filters).catch(error => {
            throw (thunkApi.rejectWithValue(error))
        })
    },
)

export const GetNotificationStatusById = createAsyncThunk(
    'notificationStatus/GetById',
    async (id: string, thunkApi): Promise<NotificationStatusDTO> => {
        const notificationStatusService = NewNotificationStatusService()

        return notificationStatusService.GetNotificationStatusById(id).catch(error => {
            throw (thunkApi.rejectWithValue(error))
        })
    },
)

const NotificationStatusSlice = createSlice({
    name: 'notificationStatus/slice',
    initialState,
    reducers: {
        setOnEdit: (state, action) => {
            state.onEdit = action.payload
        },

        // REQUEST
        setNotificationStatusIdToUpdate: (state, action) => {
            state.notificationStatusIdToUpdate = action.payload
        },
        setNotificationStatusUpdateNotificationProtocolId: (state, action) => {
            state.notificationStatusUpdateRequest.notificationProtocolId = action.payload
        },
        setNotificationStatusUpdateExpirationType: (state, action) => {
            state.notificationStatusUpdateRequest.expirationType = action.payload
        },
        setNotificationStatusUpdateCertifiedSubsequentNotificationIssue: (state, action) => {
            state.notificationStatusUpdateRequest.certifiedSubsequentNotificationIssue = action.payload
        },
        setNotificationStatusUpdateCertifiedNonCompliantNotification: (state, action) => {
            state.notificationStatusUpdateRequest.certifiedNonCompliantNotification = action.payload
        },
        setNotificationStatusUpdateCertidiedOtherCauses: (state, action) => {
            state.notificationStatusUpdateRequest.certifiedOtherCauses = action.payload
        },
        setNotificationStatusUpdateCertifiedNote: (state, action) => {
            state.notificationStatusUpdateRequest.certifiedNote = action.payload
        },
        setNotificationStatusUpdateNotEmittedCertifiedId: (state, action) => {
            state.notificationStatusUpdateRequest.notEmittedCertifiedId = action.payload
        },
        setNotificationStatusUpdateDocumentIntegrationRequestId: (state, action) => {
            state.notificationStatusUpdateRequest.documentIntegrationRequestId = action.payload
        },
        setNotificationStatusUpdateVariationCauses: (state, action) => {
            state.notificationStatusUpdateRequest.variationCauses = action.payload
        },
        setNotificationStatusUpdateNote: (state, action) => {
            state.notificationStatusUpdateRequest.note = action.payload
        },
        setNotificationStatusUpdateNotificationValidation: (state, action) => {
            state.notificationStatusUpdateRequest.notificationValidation = action.payload
        },
        setNotificationStatusUpdateValidationDate: (state, action) => {
            state.notificationStatusUpdateRequest.validationDate = action.payload
        },
        setNotificationStatusUpdateMotivation: (state, action) => {
            state.notificationStatusUpdateRequest.motivation = action.payload
        },
        setNotificationStatusUpdateAdditionalNote: (state, action) => {
            state.notificationStatusUpdateRequest.additionalNote = action.payload
        },
        setNotificationStatusUpdateOfficerId: (state, action) => {
            state.notificationStatusUpdateRequest.officerId = action.payload
        },
        setNotificationStatusUpdateRequest: (state, action) => {
            state.notificationStatusUpdateRequest = action.payload
        },
        resetNotificationStatusUpdateRequest: (state) => {
            state.notificationStatusUpdateRequest = {}
        },

        // FILTERS
        setNotificationStatusFiltersItemsPerPage: (state, action) => {
            state.getAllNotificationStatusFilters.itemsPerPage = action.payload
        },
        setNotificationStatusFiltersPage: (state, action) => {
            state.getAllNotificationStatusFilters.page = action.payload
        },
        setNotificationStatusFiltersOrder: (state, action) => {
            state.getAllNotificationStatusFilters.order = action.payload
        },
        setNotificationStatusFiltersSort: (state, action) => {
            state.getAllNotificationStatusFilters.sort = action.payload
        },
        setNotificationStatusFiltersNotificationId: (state, action) => {
            state.getAllNotificationStatusFilters.notificationId = action.payload
        },
        resetNotificationStatusFilters: (state) => {
            state.getAllNotificationStatusFilters = {
                itemsPerPage: 0,
                page: 0
            }
        },

        // STATUS
        resetNotificationStatusUpdateStatus: (state) => {
            state.notificationStatusUpdateStatus = 'idle'
        },
        resetGetAllNotificationStatusStatus: (state) => {
            state.getAllNotificationStatusStatus = 'idle'
        },
        resetGetNotificationStatusByIdStatus: (state) => {
            state.getNotificationStatusByIdStatus = 'idle'
        },

        // RESPONSE
        setGetNotificationStatusByIdResponse: (state, action) => {
            state.getNotificationStatusByIdResponse = action.payload
        }
    },
    extraReducers(builder) {
        builder

            // UPDATE
            .addCase(NotificationStatusUpdate.pending, (state) => {
                state.notificationStatusUpdateStatus = 'loading'
            })
            .addCase(NotificationStatusUpdate.fulfilled, (state) => {
                state.notificationStatusUpdateStatus = 'successfully'
            })
            .addCase(NotificationStatusUpdate.rejected, (state) => {
                state.notificationStatusUpdateStatus = 'failed'
            })

            // GET ALL
            .addCase(GetAllNotificationStatus.pending, (state) => {
                state.getAllNotificationStatusStatus = 'loading'
            })
            .addCase(GetAllNotificationStatus.fulfilled, (state, action) => {
                state.getAllNotificationStatusStatus = 'successfully'
                state.getAllNotificationStatusResponse = action.payload
            })
            .addCase(GetAllNotificationStatus.rejected, (state) => {
                state.getAllNotificationStatusStatus = 'failed'
            })

            // GET BY ID
            .addCase(GetNotificationStatusById.pending, (state) => {
                state.getNotificationStatusByIdStatus = 'loading'
            })
            .addCase(GetNotificationStatusById.fulfilled, (state, action) => {
                state.getNotificationStatusByIdStatus = 'successfully'
                state.getNotificationStatusByIdResponse = action.payload
            })
            .addCase(GetNotificationStatusById.rejected, (state) => {
                state.getNotificationStatusByIdStatus = 'failed'
            })
    },
})

export const {
    setOnEdit,

    // REQUEST
    setNotificationStatusIdToUpdate,
    setNotificationStatusUpdateAdditionalNote,
    setNotificationStatusUpdateCertidiedOtherCauses,
    setNotificationStatusUpdateCertifiedNonCompliantNotification,
    setNotificationStatusUpdateCertifiedNote,
    setNotificationStatusUpdateCertifiedSubsequentNotificationIssue,
    setNotificationStatusUpdateDocumentIntegrationRequestId,
    setNotificationStatusUpdateExpirationType,
    setNotificationStatusUpdateMotivation,
    setNotificationStatusUpdateNotEmittedCertifiedId,
    setNotificationStatusUpdateNote,
    setNotificationStatusUpdateNotificationProtocolId,
    setNotificationStatusUpdateNotificationValidation,
    setNotificationStatusUpdateOfficerId,
    setNotificationStatusUpdateValidationDate,
    setNotificationStatusUpdateVariationCauses,
    setNotificationStatusUpdateRequest,
    resetNotificationStatusUpdateRequest,

    // FILTERS
    setNotificationStatusFiltersItemsPerPage,
    setNotificationStatusFiltersNotificationId,
    setNotificationStatusFiltersOrder,
    setNotificationStatusFiltersPage,
    setNotificationStatusFiltersSort,
    resetNotificationStatusFilters,

    // STATUS
    resetGetAllNotificationStatusStatus,
    resetGetNotificationStatusByIdStatus,
    resetNotificationStatusUpdateStatus,

    // RESPONSE
    setGetNotificationStatusByIdResponse
} = NotificationStatusSlice.actions

export default NotificationStatusSlice.reducer